import { useState, useEffect, useRef, useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { isTouchDevice } from '../utils/deviceDetection';

// issue #18: pdfjs worker を self-host (CDN .mjs が iOS Safari で起動失敗するため)。
// usePdfBackground.ts:6 と同一。
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdfjs/pdf.worker.min.mjs';

const THUMB_WIDTH = 140; // px (低解像度・iOS Safari canvas メモリ配慮)

// issue #176: サムネ生成の同時 render 数の上限。高DPIスキャン画像PDFは pdf.js が元画像を
// native 解像度でデコードするため (1ページ ≈ 数百MB)、無制限に並行 render すると瞬間メモリ
// ピークが端末上限を超えてクラッシュする。同時実行をキャップして瞬間ピークを抑える。
// メモリの厳しいモバイル (touch) はさらに 1 まで絞る。
const MAX_CONCURRENT_RENDERS_DESKTOP = 2;
const MAX_CONCURRENT_RENDERS_TOUCH = 1;

interface PdfThumbnails {
  pageCount: number;
  thumbnails: Map<number, string>;
  ensureThumbnail: (pageNumber: number) => void;
  error: boolean;
  // issue #60: doc を open するたびに増える世代番号。pdfBlob 差し替え後に呼び出し側が
  // 遅延描画トリガ (IntersectionObserver) を張り直すための signal (2 回目白紙バグ対策)。
  generation: number;
}

// issue #60: PDF を1回だけ open し、ページごとに遅延描画したサムネイル objectURL を提供する。
// 全ページ即時描画は iOS Safari の canvas メモリ上限に抵触するため、可視ページのみ描画する
// (呼び出し側が IntersectionObserver で ensureThumbnail を叩く)。
export function usePdfThumbnails(pdfBlob: Blob | null): PdfThumbnails {
  const [pageCount, setPageCount] = useState(0);
  const [thumbnails, setThumbnails] = useState<Map<number, string>>(new Map());
  const [error, setError] = useState(false);
  const [generation, setGeneration] = useState(0);

  const docRef = useRef<pdfjsLib.PDFDocumentProxy | null>(null);
  const urlsRef = useRef<Map<number, string>>(new Map());
  const inFlightRef = useRef<Set<number>>(new Set());
  // issue #176: セマフォ。queueRef=描画待ちページ、runningRef=実行中 render 数、
  // pumpRef=最新 pump への間接参照 (ensureThumbnail の identity を安定させるため)。
  const queueRef = useRef<number[]>([]);
  const runningRef = useRef(0);
  const pumpRef = useRef<() => void>(() => {});
  // 同時 render 上限。デバイス判定は初回参照時に一度だけ確定させてキャッシュする。
  const maxConcurrentRef = useRef<number | null>(null);

  useEffect(() => {
    setThumbnails(new Map());
    setError(false);
    urlsRef.current = new Map();
    inFlightRef.current = new Set();
    queueRef.current = [];
    runningRef.current = 0;
    if (!pdfBlob) {
      setPageCount(0);
      return;
    }

    let cancelled = false;
    (async () => {
      const buf = await pdfBlob.arrayBuffer();
      const doc = await pdfjsLib.getDocument({ data: buf }).promise;
      if (cancelled) {
        doc.destroy();
        return;
      }
      docRef.current = doc;
      setPageCount(doc.numPages);
      // 新しい doc が使える状態になった後に世代を進める (docRef を張った後なので
      // 再発火した ensureThumbnail が確実に新 doc を掴める)。
      setGeneration((g) => g + 1);
    })().catch((err) => {
      if (isRenderingCancelled(err)) return;
      if (!cancelled) setError(true);
    });

    return () => {
      cancelled = true;
      urlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      urlsRef.current.clear();
      docRef.current?.destroy();
      docRef.current = null;
    };
  }, [pdfBlob]);

  // 1ページ分の実描画。canvas は 140px 相当に縮小し、描画後に width/height=0 で解放する。
  const renderPage = useCallback(
    async (doc: pdfjsLib.PDFDocumentProxy, pageNumber: number) => {
      const page = await doc.getPage(pageNumber);
      const base = page.getViewport({ scale: 1 });
      const scale = THUMB_WIDTH / base.width;
      const viewport = page.getViewport({ scale });
      const canvas = document.createElement('canvas');
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      await page.render({ canvas, canvasContext: ctx, viewport }).promise;
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob ? canvas.toBlob(resolve, 'image/png') : resolve(null),
      );
      canvas.width = 0;
      canvas.height = 0;
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      urlsRef.current.set(pageNumber, url);
      setThumbnails(new Map(urlsRef.current));
    },
    [],
  );

  // キューを cap 本まで並行で流す。1本終わるたびに再帰的に次を取り出す (セマフォ)。
  const pump = useCallback(() => {
    const doc = docRef.current;
    if (!doc) return;
    if (maxConcurrentRef.current == null) {
      maxConcurrentRef.current = isTouchDevice()
        ? MAX_CONCURRENT_RENDERS_TOUCH
        : MAX_CONCURRENT_RENDERS_DESKTOP;
    }
    const cap = maxConcurrentRef.current;
    while (runningRef.current < cap && queueRef.current.length > 0) {
      const pageNumber = queueRef.current.shift() as number;
      runningRef.current += 1;
      renderPage(doc, pageNumber)
        .catch((err) => {
          // 失敗ページは inFlight から外して再要求で再試行できるようにする。
          inFlightRef.current.delete(pageNumber);
          if (!isRenderingCancelled(err)) setError(true);
        })
        .finally(() => {
          runningRef.current = Math.max(0, runningRef.current - 1);
          pumpRef.current();
        });
    }
  }, [renderPage]);
  pumpRef.current = pump;

  const ensureThumbnail = useCallback((pageNumber: number) => {
    const doc = docRef.current;
    if (!doc) return;
    if (urlsRef.current.has(pageNumber) || inFlightRef.current.has(pageNumber)) return;
    inFlightRef.current.add(pageNumber);
    queueRef.current.push(pageNumber);
    pumpRef.current();
  }, []);

  return { pageCount, thumbnails, ensureThumbnail, error, generation };
}

function isRenderingCancelled(err: unknown): boolean {
  return (
    err instanceof pdfjsLib.RenderingCancelledException ||
    (err as { name?: string } | undefined)?.name === 'RenderingCancelledException'
  );
}
